/**
 * /platform/roles - list of every role visible to the caller.
 *
 * Visibility (enforced server-side):
 *   * super_admin sees every system role + every partner-custom role.
 *   * partner_admin sees system roles + customs at-or-below their
 *     partner_path (ltree).
 *
 * Each row links to /platform/roles/:id which shows the role's full
 * permission grants grouped by category.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Plus, ShieldCheck, Users } from "lucide-react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePageTitle } from "@/lib/use-page-title";
import { cn } from "@/lib/utils";

export function RolesPage(): JSX.Element {
  usePageTitle("Roles & permissions");

  const query = useQuery({
    queryKey: ["roles", "list"],
    queryFn: () => apiClient.listRoles({ include_system: true }),
    retry: false,
  });

  const roles = query.data?.roles ?? [];
  const systemRoles = roles.filter((r) => r.is_system);
  // V0074 - split non-system rows by tier:
  //   * partner_id=null  → platform-custom (super_admin-authored)
  //   * partner_id!=null → partner-custom
  const customRoles = roles.filter((r) => !r.is_system);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Roles &amp; permissions
          </h1>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
            System roles ship with the platform and are managed by migrations. Custom roles let a
            partner tailor permission bundles for their own people. Click a row to see the full
            permission grants.
          </p>
        </div>
        <Link
          to="/platform/roles/new"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          New role
        </Link>
      </div>

      {query.isLoading && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading roles…
        </p>
      )}
      {query.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          {describe(query.error)}
        </div>
      )}

      {!query.isLoading && !query.isError && (
        <>
          <RoleSection
            heading="System roles"
            subtitle="Platform-managed. Shared across every partner. Permission bundles change only via migration."
            roles={systemRoles}
          />
          <RoleSection
            heading="Custom roles"
            subtitle="Partner-tailored permission bundles. Create a new role with the button above, then assign permissions on the detail page."
            roles={customRoles}
            emptyHint="No custom roles yet. Use 'New role' to create one."
          />
        </>
      )}
    </div>
  );
}

function RoleSection(props: {
  readonly heading: string;
  readonly subtitle: string;
  readonly roles: ReadonlyArray<{
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly description: string | null;
    readonly partner_id: string | null;
    readonly partner_name: string | null;
    readonly is_system: boolean;
    readonly permission_count: number;
    readonly member_count: number;
  }>;
  readonly emptyHint?: string;
}): JSX.Element {
  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-sm font-semibold tracking-tight">{props.heading}</h2>
        <p className="text-xs text-muted-foreground">{props.subtitle}</p>
      </div>
      {props.roles.length === 0 ? (
        <p className="rounded-md border border-dashed bg-muted/30 p-4 text-xs text-muted-foreground">
          {props.emptyHint ?? "No roles in this section."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5">Role</th>
                <th className="px-4 py-2.5">Tier</th>
                <th className="px-4 py-2.5 text-right">Permissions</th>
                <th className="px-4 py-2.5 text-right">People</th>
                <th className="px-4 py-2.5 text-right" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {props.roles.map((r) => (
                <tr key={r.id} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-2 align-middle">
                    <div className="flex items-center gap-2">
                      <ShieldCheck
                        className={cn(
                          "h-4 w-4",
                          r.is_system
                            ? "text-indigo-600"
                            : r.partner_id === null
                              ? "text-purple-600"
                              : "text-emerald-600",
                        )}
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{r.name}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{r.slug}</div>
                        {r.description && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {r.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 align-middle text-xs">
                    <RoleTierBadge role={r} />
                  </td>
                  <td className="px-4 py-2 text-right align-middle font-mono tabular-nums">
                    {r.permission_count}
                  </td>
                  <td className="px-4 py-2 text-right align-middle">
                    <span className="inline-flex items-center gap-1 font-mono text-xs tabular-nums">
                      <Users className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                      {r.member_count}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right align-middle">
                    <Link
                      to={`/platform/roles/${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Tier badge - three values per V0074:
 *    * is_system=true                    → "System" (indigo)
 *    * is_system=false, partner_id=null  → "Platform" (purple)
 *    * is_system=false, partner_id!=null → "Partner: <name>" (emerald)
 */
function RoleTierBadge(props: {
  readonly role: {
    readonly is_system: boolean;
    readonly partner_id: string | null;
    readonly partner_name: string | null;
  };
}): JSX.Element {
  const { is_system, partner_id, partner_name } = props.role;
  if (is_system) {
    return (
      <span className="inline-flex items-center rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
        System
      </span>
    );
  }
  if (partner_id === null) {
    return (
      <span className="inline-flex items-center rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple-800">
        Platform
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800">
      Partner: {partner_name ?? "-"}
    </span>
  );
}
